import {
  restrictedBLimitsStates,
  restrictedCLimitsStates,
  restrictedDLimitsStates,
  unlimitedDLimitsStates,
  UNLIMITED_D_VALUE
} from '@ourbranch/lookups';

import isPlainObject from 'lodash/isPlainObject';
import forOwn from 'lodash/forOwn';

const coordinateLookup = {
  north: 'N',
  south: 'S',
  east: 'E',
  west: 'W'
};

const formatCoordinates = (coordinates) => {
  return coordinates && coordinates !== 'unknown' ? ` ${coordinateLookup[coordinates]}` : '';
};

const joinValidParts = (parts, joinString = ', ') => {
  return parts.filter(Boolean).join(joinString).trim();
};

export const formatLocationAddress = (inputAddress, legalDescription) => {
  const { format } = legalDescription;
  const { address, address2, city, state, zip, county } = inputAddress;
  const cityStateString = joinValidParts([city, state], ' ');
  const countyString = county ? `${county} County` : null;

  if (format === 'street') {
    const streetAddress = joinValidParts([address, address2], ' ');
    return joinValidParts([streetAddress, cityStateString, zip]);
  }

  if (format === 'section-township-range') {
    const { section, township, townshipCoordinates, range, rangeCoordinates } = legalDescription;
    const sectionString = section ? `SEC ${section}` : null;
    const townshipString = township ? `TWNSHP ${township}${formatCoordinates(townshipCoordinates)}` : null;
    const rangeString = range ? `RNG ${range}${formatCoordinates(rangeCoordinates)}` : null;
    return joinValidParts([cityStateString, zip, countyString, sectionString, townshipString, rangeString]);
  }

  if (format === 'lot-block') {
    const { lot, block } = legalDescription;
    const lotString = lot ? `LOT ${lot}` : null;
    const blockString = block ? `BLK ${block}` : null;
    return joinValidParts([cityStateString, zip, lotString, blockString]);
  }

  if (format === 'long-legal') {
    return joinValidParts([cityStateString, zip, countyString]);
  }
};

export const isCountyIncludedInFormattedAddress = (legalDescription) => {
  if (!legalDescription?.format) {
    return false;
  }

  // County is included in these formats:
  return legalDescription.format === 'section-township-range' || legalDescription.format === 'long-legal';
};

export const buildOptions = (value, letter, state, numFamiliesInDwelling) => {
  let min = 0;
  let max = 100;
  switch (letter) {
    case 'B':
      min = 0;
      if (restrictedBLimitsStates.includes(state)) {
        max = 10;
      } else {
        max = 100;
      }
      break;
    case 'C':
      min = 0;
      if (state === 'MD') {
        min = 40;
        max = 75;
      } else if (restrictedCLimitsStates.includes(state)) {
        if (!numFamiliesInDwelling || numFamiliesInDwelling <= 2) {
          max = 60;
        } else if (numFamiliesInDwelling === 3) {
          max = 40;
        } else {
          max = 35;
        }
      } else {
        max = 100;
      }
      break;
    case 'D':
      if (restrictedDLimitsStates.includes(state)) {
        // these states fix ALE (CovD) at 10% of CovA
        min = 10;
        max = 10;
      } else {
        min = 0;
        max = 40;
      }
      break;
  }
  const array = [];
  for (let num = min; num <= max; num += 5) {
    const optionValue = (num / 100) * value;
    array.push({ id: optionValue, value: value ? `${num}% ($${optionValue.toLocaleString('en-US')})` : `${num}%` });
  }

  if (letter === 'D' && unlimitedDLimitsStates.includes(state)) {
    array.push({ id: UNLIMITED_D_VALUE, value: 'Unlimited for up to 24 months' });
  }

  return array;
};

export const formatCoverageB = (coverages) => {
  const { coverageA, coverageB } = coverages || {};
  if (typeof coverageA !== 'number' || typeof coverageB !== 'number') {
    return '';
  }
  const percent = ((coverageB / coverageA) * 100).toFixed(0);
  return `${percent}% ($${coverageB.toLocaleString('en-US')})`;
};

/**
 * Converts deductible percentage values to dollar amounts based on coverage limit.
 * Forreston brand in Illinois uses percentage-based deductibles (0.01, 0.015, 0.02)
 * that need to be calculated against the structure's coverage limit.
 *
 * @param {number} deductibleValue - The deductible value (either percentage < 1 or dollar amount >= 1)
 * @param {number} coverageLimit - The coverage limit to calculate percentage against
 * @returns {number} - The dollar amount (either calculated from percentage or original value)
 */
export const calculateDeductibleAmount = (deductibleValue, coverageLimit) => {
  // If no deductible value provided, return 0
  if (!deductibleValue && deductibleValue !== 0) {
    return 0;
  }

  // If no coverage limit provided, return the original value
  // (can't calculate percentage without a base)
  if (!coverageLimit) {
    return deductibleValue;
  }

  // Values < 1 are percentages (e.g., 0.01 = 1%)
  // Values >= 1 are already dollar amounts
  if (deductibleValue < 1) {
    return Math.round(coverageLimit * deductibleValue);
  }

  return deductibleValue;
};

const flattenObject = (obj, parentKey = '', result = {}) => {
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const newKey = parentKey ? `${parentKey}[${index}]` : `[${index}]`;
      flattenObject(item, newKey, result);
    });
  } else if (isPlainObject(obj)) {
    forOwn(obj, (v, k) => {
      const newKey = parentKey ? `${parentKey}.${k}` : k;
      flattenObject(v, newKey, result);
    });
  } else {
    result[parentKey] = obj;
  }

  return result;
};

/**
 * Check if any field within a given fieldName path has been touched
 * @param {Object} touched - Formik touched object
 * @param {string} fieldName - The field path to check (e.g., "farmLocations[0].risks[1]")
 * @returns {boolean} - True if any field in the path is touched
 */
export const isFieldPathTouched = (touched, fieldName) => {
  if (!touched || !fieldName) {
    return false;
  }

  const flattenedTouched = flattenObject(touched);

  const isRiskField = fieldName.includes('risks');

  if (Object.keys(flattenedTouched).some((key) => key.includes('adjustmentFactor'))) {
    return fieldName === 'FarmNavigator';
  }

  if (isRiskField) {
    return Object.keys(flattenedTouched).some((key) => key.includes(fieldName));
  }

  return Object.keys(flattenedTouched).some((key) => key.includes(fieldName) && !key.includes('risks'));
};

export function getSupportedExteriorWallType(constructionType) {
  const mapping = {
    L: 'W',
    MD: 'S',
    S: 'T'
  };

  return mapping[constructionType];
}
